import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import type { ReceiptTableRowModel } from '@pflegenavi/transaction-components';
import { formatReceiptTableRowModel } from '@pflegenavi/transaction-components';
import {
  Link,
  makeConfig,
  PaginatedTable2,
  ReceiptStatusLabel,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { generatePath, useNavigate } from 'react-router-dom';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import {
  useGetPaymentProcessor,
  useReceiptsPhoenix,
} from '@pflegenavi/frontend/api-nursing-home';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { Stack, Typography } from '@mui/material';
import { useTrackStructuredEvent } from '@pflegenavi/frontend/tracking';

type ReceiptTableColumnNames =
  | 'residentName'
  | 'receiptType'
  | 'serviceProvider'
  | 'amount'
  | 'additional-fee-amount'
  | 'submission-date'
  | 'receipt-date'
  | 'status'
  | 'receipt';
type ReceiptTableFilters = never;
const ReceiptStatus: FC<{ row: ReceiptTableRowModel }> = ({ row }) => {
  const receiptStatus = useMemo(
    () => (row.cancellation ? 'Cancelled' : row.status),
    [row.cancellation, row.status]
  );

  return <ReceiptStatusLabel status={receiptStatus} />;
};
export const ReceiptTable: FC<{ groupId: string | undefined }> = ({
  groupId,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();
  const paymentProcessor = useGetPaymentProcessor();

  const navigate = useNavigate();

  const { selectedNursingHome } = useNursingHomeContext();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  const { data: receipts, isLoading } = useReceiptsPhoenix(
    {
      nursing_home_ids: selectedNursingHome ? [selectedNursingHome.id] : [],
      page_size: pageSize,
      page: page + 1,
      cash_list_transaction_group_ids: groupId ? [groupId] : [],
    },
    {
      enabled: groupId !== undefined,
    }
  );

  const formatted = useMemo(() => {
    if (!receipts) {
      return [];
    }
    return receipts.data.map((receipt) =>
      formatReceiptTableRowModel(receipt, { fDate, fCurrencyCents, t })
    );
  }, [fCurrencyCents, fDate, t, receipts]);

  const onNavigateUser = useCallback(
    (residentId: string) => {
      navigate(
        generatePath(nhAppMainPages.RESIDENT_DETAIL_ACCOUNT, {
          resident_id: residentId,
        })
      );
    },
    [navigate]
  );

  const config = useMemo(
    () =>
      makeConfig<
        ReceiptTableRowModel,
        ReceiptTableColumnNames,
        ReceiptTableFilters
      >({
        showAll: undefined,
        onClickRow: (row) => {
          navigate(
            generatePath(nhAppMainPages.TRANSACTIONS_EDIT, {
              transaction_id: row.id,
            })
          );
        },
        columns: [
          {
            key: 'residentName',
            label: t('residents.main'),
            align: 'left',
            component: ({ row }) => (
              <Link
                to={'/'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onNavigateUser(row.residentId);
                }}
              >
                <Typography variant="subtitle2" noWrap>
                  {row.residentNameString}
                </Typography>
              </Link>
            ),
          },
          {
            key: 'receiptType',
            label: t('receipts.type'),
            align: 'left',
            component: ({ row }) => row.receiptTypeString,
          },
          {
            key: 'serviceProvider',
            label: t('receipts.serviceProvider'),
            align: 'left',
            component: ({ row }) => row.serviceProviderString,
          },
          {
            key: 'amount',
            label: t('resident.form.amount'),
            align: 'right',
            component: ({ row }) => row.amountString,
          },
          ...(paymentProcessor === 'mangopay'
            ? [
                {
                  key: 'additional-fee-amount' as ReceiptTableColumnNames,
                  label: t('resident.form.additional-fee-amount'),
                  align: 'right' as const,
                  component: ({ row }: { row: ReceiptTableRowModel }) =>
                    row.additionalFeeAmount !== 0
                      ? row.additionalFeeAmountString
                      : '--',
                },
              ]
            : []),
          {
            key: 'submission-date',
            label: t('common.submission-date'),
            align: 'center',
            component: ({ row }) => row.submissionDateString ?? '--',
          },
          {
            key: 'receipt-date',
            label: t('common.receipt-date'),
            align: 'center',
            component: ({ row }) => row.dateString,
          },
          {
            key: 'status',
            label: t('common.status'),
            align: 'left',
            component: ReceiptStatus,
          },
        ],
        pagination: {
          page,
          pageSize,
          pageSizeOptions: [5, 10],
          total: receipts?.meta.total_count || 0,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        },
      }),
    [t, receipts, page, pageSize, navigate, onNavigateUser, paymentProcessor]
  );

  if (!receipts || receipts.data.length === 0) {
    return null;
  }

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        <Typography variant="h6" fontWeight="bold">
          {t('cashManagement.group-detail.overview.linked-receipts-title')}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {t('cashManagement.link.table-row.link-count.receipt', {
            count: receipts?.meta.total_count || 0,
          })}
        </Typography>
      </Stack>

      <PaginatedTable2
        name="CashTransactionGroupDetailReceiptTable"
        useTrackStructuredEvent={useTrackStructuredEvent}
        config={config}
        rows={formatted ?? []}
        isLoading={isLoading}
      />
    </Stack>
  );
};
